import {ActivatedRouteSnapshot, ResolveFn} from '@angular/router';
import {RouteOptions} from '../../../models/route-options/route-options';
import {combineLatest, map, switchMap} from 'rxjs';
import {resolveGovernmentIdDataset} from '../../../services/resolvers/government-id-dataset.resolver';
import {DatasetService} from '../../../services/api/fit-api/dataset.service';
import {inject} from '@angular/core';
import {RouteOption} from '../../../models/route-options/route-option';
import {
	getEndYearRouteOption,
	getStartYearRouteOption,
	resolveFinancialHealthIndicator
} from './financial-health-indicator.resolver.service';
import {Indicator} from '../../../services/api/fit-api/models/indicators/indicator';
import {DisplayIndicatorNetRouteOption} from '../../../models/route-options/display-indicator-net-route-option';
import {
	DisplayIndicatorBenchmarkRouteOption
} from '../../../models/route-options/display-indicator-benchmark-route-option';
import {DisplayGovTypeMeanRouteOption} from '../../../models/route-options/display-gov-type-mean-route-option';
import {DisplayGovTypeMedianRouteOption} from '../../../models/route-options/display-gov-type-median-route-option';
import {DisplayGovTypeTrimeanRouteOption} from '../../../models/route-options/display-gov-type-trimean-route-option';
import {resolveGovernment} from '../../../services/resolvers/local-government.resolver';
import {LocalGovernment} from '../../../services/api/fit-api/models/local-government';

/**
 * Configures all the options for the indicator drill down page:
 *  * Options Dialog - Year Range (start and end)
 *  * Options Dialog - Show Indicator Net - boolean
 *  * Options Dialog - Show Benchmark - boolean
 *  * Options Dialog - Show Gov Type Stats (Mean, Median, Trimean) - booleans
 *
 * Uses the new, definitive pattern for setting the route options and all required input reference data needed by each option
 *
 * @param route
 */
export const financialHealthIndicatorOptionsResolver: ResolveFn<RouteOptions> = route  => {
	return resolveFinancialHealthIndicatorOptions(route);
};

export function resolveFinancialHealthIndicatorOptions(route: ActivatedRouteSnapshot) {
	const datasetService = inject(DatasetService);
	const datasetDetail = resolveGovernmentIdDataset(route).pipe(switchMap(dataset => {
		if (dataset === null) {
			throw new Error("Dataset not found.  Should have been caught by the route guard(s).");
		}
		return datasetService.getDatasetDetail(dataset?.datasetType, dataset.id);
	}));

	return combineLatest([
		datasetDetail,
		resolveFinancialHealthIndicator(route),
		resolveGovernment(route)
	]).pipe(
		map(([dd, indicator, government]) => {
			return new RouteOptions([
					getStartYearRouteOption(route, [dd]),
					getEndYearRouteOption(route, [dd]),
					...getIndicatorDisplayRouteOptions(route, indicator, government)
				]);
			})
	);
}

// todo move these functions to a centralized location
function getIndicatorDisplayRouteOptions(
	route: ActivatedRouteSnapshot,
	indicator: Indicator | null,
	government: LocalGovernment
): Array<RouteOption> {
	const displayOptions = new Array<RouteOption>();

	if (indicator) {
		// note: for indicators with net values, turn net line series 'off' by default
		// per https://youtrack.thinkfellow.com/youtrack/issue/FIT-2385
		if (indicator.calculations.some(c => c.net !== null)) {
			displayOptions.push(new DisplayIndicatorNetRouteOption(route, false));
		}
		displayOptions.push(new DisplayIndicatorBenchmarkRouteOption(route, true));

		displayOptions.push(new DisplayGovTypeMeanRouteOption(route, false, getGovTypeAnnotation(indicator, government)));
		displayOptions.push(new DisplayGovTypeMedianRouteOption(route, false, getGovTypeAnnotation(indicator, government)));
		displayOptions.push(new DisplayGovTypeTrimeanRouteOption(route, false, getGovTypeAnnotation(indicator, government)));
	}

	return displayOptions;
}

function getGovTypeAnnotation(indicator: Indicator, government: LocalGovernment) {
	const basis = indicator.accountingBasis ?? '';
	const govTypeDesc = government.govTypeDescPlural;

	return `${basis} ${govTypeDesc}:`;
}
